import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Module-level cache so the ~2.6k Hitchcock name map is fetched only once.
let NAMES_CACHE = null;
let NAMES_PROMISE = null;

export const loadNameMeanings = () => {
  if (NAMES_CACHE) return Promise.resolve(NAMES_CACHE);
  if (!NAMES_PROMISE) {
    NAMES_PROMISE = axios
      .get(`${BACKEND_URL}/api/dictionary/names`)
      .then((r) => { NAMES_CACHE = r.data?.names || {}; return NAMES_CACHE; })
      .catch(() => { NAMES_CACHE = {}; return NAMES_CACHE; });
  }
  return NAMES_PROMISE;
};

const WORD_RE = /[A-Za-zÀ-ÿ'’]+/g;

// Session cache of Easton's "Bible people" mini-profiles, keyed by name.
const PROFILE_CACHE = {};
export const loadProfile = async (name) => {
  const key = (name || '').toLowerCase();
  if (PROFILE_CACHE[key]) return PROFILE_CACHE[key];
  try {
    const r = await axios.get(`${BACKEND_URL}/api/dictionary/search`, {
      params: { term: name, dictionary: 'eastons' },
    });
    const e = r.data?.entries?.[0] || {};
    PROFILE_CACHE[key] = {
      definition: e.definition || 'No profile available.',
      refs: Array.isArray(e.scripture_refs) ? e.scripture_refs : [],
    };
  } catch (e) {
    PROFILE_CACHE[key] = { definition: 'No profile available.', refs: [] };
  }
  return PROFILE_CACHE[key];
};

// Parse a "Book Chapter:Verse" reference into parts. Handles multi-word book
// names ("1 Samuel"), chapter-only ("Genesis 12"), and ranges ("Acts 7:2-4").
export const parseRef = (reference) => {
  if (!reference) return null;
  const m = reference.match(/^(.+?)\s+(\d+)(?::(\d+))?/);
  if (!m) return null;
  return { book: m[1].trim(), chapter: parseInt(m[2], 10), verse: m[3] ? parseInt(m[3], 10) : null };
};

// KJV / Greek-form genealogy spellings → the canonical Hitchcock key. Lets the
// name-meanings feature recognize names like "Phares" (Perez) or "Booz" (Boaz)
// that appear in Matthew 1 / Luke 3 genealogies under older transliterations.
const NAME_ALIASES = {
  phares: 'perez', zara: 'zerah', esrom: 'hezron', aram: 'ram', naasson: 'nahshon',
  booz: 'boaz', roboam: 'rehoboam', abia: 'abijah', josaphat: 'jehoshaphat',
  joram: 'jehoram', ozias: 'uzziah', joatham: 'jotham', achaz: 'ahaz',
  ezekias: 'hezekiah', manasses: 'manasseh', josias: 'josiah', jechonias: 'jeconiah',
  salathiel: 'shealtiel', zorobabel: 'zerubbabel', abiud: 'abihud', sadoc: 'zadok',
  thamar: 'tamar', rachab: 'rahab', urias: 'uriah', core: 'korah', noe: 'noah',
  elias: 'elijah', eliseus: 'elisha', esaias: 'isaiah', jeremias: 'jeremiah',
  jonas: 'jonah', osee: 'hosea', sem: 'shem', cham: 'ham', henoch: 'enoch',
  mathusala: 'methusaleh', ragau: 'reu', phalec: 'peleg', heber: 'eber', sala: 'salah',
  juda: 'judah', thara: 'terah', nachor: 'nahor', saruch: 'serug', japheth: 'japhet',
};

// Resolve a word to a name entry, trying the word itself, its possessive form
// ("David's", "Moses'"), and KJV genealogy aliases. Returns {name, meaning} or null.
export const resolveName = (word, namesMap) => {
  if (!word || !namesMap) return null;
  const slug = word.toLowerCase();
  if (namesMap[slug]) return namesMap[slug];
  const base = slug.replace(/['’]s?$/, '');
  if (base !== slug && namesMap[base]) return namesMap[base];
  const canon = NAME_ALIASES[slug] || NAME_ALIASES[base];
  if (canon && namesMap[canon]) return namesMap[canon];
  return null;
};

// Extract the distinct recognized biblical names appearing in a block of text.
export const extractNames = (text, namesMap) => {
  const found = new Map();
  if (!text || !namesMap) return [];
  let m;
  WORD_RE.lastIndex = 0;
  while ((m = WORD_RE.exec(text)) !== null) {
    const word = m[0];
    const isCapitalized = word[0] === word[0].toUpperCase() && word[0] !== word[0].toLowerCase();
    if (!isCapitalized || word.length < 3) continue;
    const entry = resolveName(word, namesMap);
    if (entry) {
      // Dedupe by the canonical name so "Phares" and "Perez" collapse into one.
      const key = entry.name.toLowerCase();
      if (!found.has(key)) found.set(key, { slug: key, name: entry.name, meaning: entry.meaning });
    }
  }
  return Array.from(found.values());
};
